import type { ComponentProps, Dispatch, SetStateAction } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import noop from 'lib/function/noop'
import type { ChatNavigationOptions } from '../hooks/useChatNavigation'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useTripPlannerMenuDrawer } from '../hooks/useTripPlannerMenuDrawer'
import TripChatMobileAppShell from '../mobile/TripChatMobileAppShell'
import TripChatMobileChatView from '../mobile/TripChatMobileChatView'
import TripChatMobileTimelineView from '../mobile/TripChatMobileTimelineView'
import DrawerContainer from '../containers/DrawerContainer'
import DownloadItineraryButton from '../components/DownloadItineraryButton'
import { useMessageContext } from '../hooks/useMessageContext'
import { useChatSessionTracking } from '../hooks/useChatSessionTracking'
import { usePromptTokenSubmission } from '../hooks/usePromptTokenSubmission'
import { useChatRouteSync } from '../hooks/useChatRouteSync'
import { addBookingItemIdsToSelection, type BookingSelection } from '../utils/bookingSelection'

interface Props {
  chatId: number | undefined
  syncChatId: (id: number | undefined, options?: ChatNavigationOptions) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
  isLoggedIn: boolean
  isStaff: boolean
  isDevMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  onStaffViewedChatIdChange: (chatId: number | undefined) => void
  onDevModeToggle: () => void
  setBookingSelection: Dispatch<SetStateAction<BookingSelection>>
  selectedBookingItemIds?: Array<string>
  prefilledOrderChipLabels: Array<string>
  onRemovePrefilledOrderTag: (tag: string) => void
}

const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

function TripChatMobileSurface({
  chatId,
  syncChatId,
  isNewChatRoute,
  isLandingRoute,
  existingRouteChatId,
  navigateToNewChatRoute,
  isLoggedIn,
  isStaff,
  isDevMode,
  isSpoofed,
  spoofingAgentEmail,
  onStaffViewedChatIdChange,
  onDevModeToggle,
  setBookingSelection,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  prefilledOrderChipLabels,
  onRemovePrefilledOrderTag,
}: Props) {
  const eventBus = useTripPlannerEventBus()
  const { hasItineraryData } = useItineraryContext()
  const { search } = useLocation()
  const { submitMessage, resetConversation, resetStreamingState, setFollowTail } = useChatActions()
  const { isStreaming, loadingState } = useChatStreamStatus()
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null)
  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const trackFirstMessage = useChatSessionTracking({ chatId, existingRouteChatId })

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [])

  const {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  } = useMessageContext({
    streamingSubmit: submitMessage,
    setFollowTail,
    setMessageInputValue: noop,
    focusChatInput,
    prefilledMultiChoiceSelection: prefilledOrderChipLabels,
  })

  const handleSubmitWithTracking = useCallback((message: string) => {
    trackFirstMessage()
    return handleSubmit(message)
  }, [handleSubmit, trackFirstMessage])

  const resetChatSpecificState = useCallback(() => {
    eventBus.dispatch({ type: 'reset_chat_state' })
    resetSelectionState()
  }, [eventBus, resetSelectionState])

  const resetConversationState = useCallback((options?: ChatNavigationOptions) => {
    syncChatId(undefined, { shouldUpdateUrl: options?.shouldUpdateUrl })
    resetConversation({ followTail: false })
    resetChatSpecificState()
  }, [resetChatSpecificState, resetConversation, syncChatId])

  const loadChatById = useCallback((selectedChatId: number, options?: ChatNavigationOptions) => {
    if (selectedChatId === chatId) {
      return
    }

    resetChatSpecificState()
    resetStreamingState()
    syncChatId(selectedChatId, options)
    setFollowTail(false)
  }, [chatId, resetChatSpecificState, resetStreamingState, setFollowTail, syncChatId])

  const handleStartNewChat = useCallback(() => {
    onStaffViewedChatIdChange(undefined)
    resetConversationState({ shouldUpdateUrl: false })
    navigateToNewChatRoute()
  }, [navigateToNewChatRoute, onStaffViewedChatIdChange, resetConversationState])

  const handleChatSelect = useCallback((selectedChatId: number) => {
    onStaffViewedChatIdChange(undefined)
    loadChatById(selectedChatId)
  }, [loadChatById, onStaffViewedChatIdChange])

  const handleChatDeleted = useCallback((deletedChatId: number, fallbackChatId: number | undefined) => {
    if (chatId !== deletedChatId) {
      return
    }

    onStaffViewedChatIdChange(undefined)
    if (fallbackChatId) {
      loadChatById(fallbackChatId)
      return
    }

    resetConversationState()
  }, [chatId, loadChatById, onStaffViewedChatIdChange, resetConversationState])

  useChatRouteSync({
    chatId,
    existingRouteChatId,
    isLandingRoute,
    isNewChatRoute,
    loadChatById,
    resetConversationState,
  })

  usePromptTokenSubmission({
    search,
    isNewChatRoute,
    isLoading,
    isStreaming,
    onPromptSubmit: handleSubmitWithTracking,
  })

  const handleBookingItemsAdded = useCallback((bookingItemIds: Array<string>) => {
    if (bookingItemIds.length === 0) {
      return
    }

    setBookingSelection(previous => addBookingItemIdsToSelection(previous, bookingItemIds))
  }, [setBookingSelection])

  const downloadItineraryAction = useMemo(() => {
    if (!hasItineraryData) {
      return undefined
    }

    return <DownloadItineraryButton chatId={chatId} />
  }, [chatId, hasItineraryData])

  const onMenuOpen = useTripPlannerMenuDrawer({
    variant: 'mobile',
    isLoggedIn,
    isStaff,
    isDevMode,
    onStartNewChat: handleStartNewChat,
    onChatSelect: handleChatSelect,
    onChatDeleted: handleChatDeleted,
    onDevModeToggle,
    downloadItineraryAction,
  })

  const tabPanels = useMemo(() => ({
    chat: (
      <TripChatMobileChatView
        chatId={chatId}
        isLoggedIn={isLoggedIn}
        isDevMode={isDevMode}
        isSpoofed={isSpoofed}
        spoofingAgentEmail={spoofingAgentEmail}
        navigateToNewChatRoute={navigateToNewChatRoute}
        selectedBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={handleBookingItemsAdded}
        handleSubmit={handleSubmitWithTracking}
        handleMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
        activeMultiChoiceSelection={activeMultiChoiceSelection}
        prefilledSelectionTags={prefilledOrderChipLabels}
        mobileSelectionText={mobileSelectionText}
        offerContextTag={offerContextTag}
        handleRemoveTag={handleRemoveTag}
        onRemovePrefilledSelectionTag={onRemovePrefilledOrderTag}
        chatInputRef={chatInputRef}
      />
    ),
    timeline: <TripChatMobileTimelineView />,
  }), [
    activeMultiChoiceSelection,
    chatId,
    handleBookingItemsAdded,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    handleSubmitWithTracking,
    isDevMode,
    isLoggedIn,
    isSpoofed,
    mobileSelectionText,
    navigateToNewChatRoute,
    offerContextTag,
    onRemovePrefilledOrderTag,
    prefilledOrderChipLabels,
    selectedBookingItemIds,
    spoofingAgentEmail,
  ])

  return (
    <TripChatMobileAppShell
      onMenuOpen={onMenuOpen}
      tabPanels={tabPanels}
      showComposerReservedSpace={false}
      showCartButton
      isCartButtonDisabled
    >
      <DrawerContainer />
    </TripChatMobileAppShell>
  )
}

export type TripChatMobileSurfaceProps = ComponentProps<typeof TripChatMobileSurface>

export default TripChatMobileSurface
